'use client';

import { useQuery } from '@tanstack/react-query';
import { useTranslations } from 'next-intl';
import GameCard from '@/components/entities/games/GameCard';
import { Icon } from '@/components/entities/Sprite/Icon';
import { ShowAllButton } from '@/components/shared';
import { SliderWrapper } from '@/components/shared/SliderWrapper';
import { AppRoutes } from '@/i18n/constants';
import { Link } from '@/i18n/routing';
import { gameQueryOptions } from '@/queries/games/games.queries';
import { useAppSelector } from '@/redux/hooks';

const FAVORITES_SLIDER_LIMIT = 9;
const FAVORITES_FETCH_LIMIT = 50;
const FAVORITES_CATEGORY_ID = 'favorites-games';

export default function FavoritesSlider() {
  const t = useTranslations('favorites-games');
  const userSession = useAppSelector(state => state.user.userSession);

  const { data: favoriteGames } = useQuery(
    gameQueryOptions.favoritesList({ params: { limit: FAVORITES_FETCH_LIMIT }, options: { enabled: !!userSession } }),
  );

  const allItems = favoriteGames?.items ?? [];
  const totalItems = favoriteGames?.totalItems ?? 0;
  const items = allItems.slice(0, FAVORITES_SLIDER_LIMIT);

  if (!userSession || totalItems === 0) {
    return null;
  }

  return (
    <div className="mb-4.25 lg:mb-4 3xl:mb-7.25">
      <Link
        href={AppRoutes.categoryId(FAVORITES_CATEGORY_ID)}
        className="inline-flex justify-between items-center mb-1.25 lg:mb-1.5 3xl:mb-1.25"
      >
        <div className="group flex items-center gap-1">
          <div className="relative flex items-center gap-1 h-6 font-medium text-[1.125rem] leading-5.5 3xl:leading-6">
            <span className="text-white">{t('title')}</span>
            <span
              aria-hidden
              className="max-lg:hidden absolute inset-0 bg-main-front-gradient text-gradient opacity-0 transition-opacity duration-200 group-hover:opacity-100 group-active:opacity-100"
            >
              {t('title')}
            </span>
            <Icon name="lobby-title-arrow" className="size-4 mt-px lg:mt-0.5 text-white transition-colors duration-200 group-hover:text-c100 group-active:text-c100" />
          </div>
        </div>
      </Link>

      <div className="relative -mx-4 overflow-x-clip lg:-mx-10 3xl:mr-0">
        <SliderWrapper>
          <div className="flex w-max pl-4 pr-3.5 gap-2 lg:pl-10 lg:pr-10 lg:gap-5.25 3xl:pl-10 3xl:pr-10 3xl:gap-5.25">
            {items.map((game, index) => (
              <div key={game.id} className="w-30! relative shrink-0">
                <GameCard
                  id={game.id}
                  name={game.name}
                  cover={game.cover}
                  path={AppRoutes.gamesId(game.id)}
                  variant="game-cover-small"
                  priority={[0, 1, 2].includes(index)}
                  isFavorited
                  likeButtonPosition="compactLg"
                  likeButtonIconSize="compactLg"
                />
              </div>
            ))}

            {totalItems > FAVORITES_SLIDER_LIMIT && (
              <div className="w-32! cursor-pointer py-1 flex items-center justify-center shrink-0">
                <ShowAllButton href={AppRoutes.categoryId(FAVORITES_CATEGORY_ID)} className="w-full" />
              </div>
            )}
          </div>
        </SliderWrapper>

        <div
          className="pointer-events-none absolute top-0 right-0 w-23 h-42 z-10 bg-[linear-gradient(270deg,rgba(35,19,51,0.8)_0%,rgba(35,19,51,0.4)_50%,rgba(35,19,51,0)_100%)]"
        />
      </div>
    </div>
  );
}
